import Blog from "@/Layouts/Blog";
import Hero from "@/Sections/Hero";
import { Head } from "@inertiajs/inertia-react";
import Contact from "@/Sections/Contact";

import { CheckIcon } from "@heroicons/react/outline";

export default function AboutPage() {
    const features = [
        {
            name: "1. Verhoogde productiviteit",
            description:
                "Fitte werknemers hebben meer energie en kunnen zich beter concentreren. Dit leidt tot een hogere productiviteit en betere werkprestaties.",
            icon: CheckIcon,
        },
        {
            name: "2. Verbeterde werksfeer",
            description:
                "Samen sporten bevordert de teamgeest en sociale cohesie binnen het bedrijf. Dit kan leiden tot betere samenwerking en een positievere werkomgeving.",
            icon: CheckIcon,
        },
        {
            name: "3. Aantrekkelijke werkgever",
            description:
                "Het aanbieden van bedrijfsfitness maakt je als werkgever aantrekkelijker voor potentiële werknemers. Het laat zien dat je investeert in de gezondheid en het welzijn van je personeel.",
            icon: CheckIcon,
        },
        {
            name: "4. Kostenbesparend op lange termijn",
            description:
                "Hoewel er initiële kosten zijn verbonden aan het opzetten van bedrijfsfitness, wegen deze op tegen de besparingen door verminderd ziekteverzuim en verhoogde productiviteit.",
            icon: CheckIcon,
        },
        {
            name: "5. Verbetering van de werk-privé balans",
            description:
                "Door sportfaciliteiten op of nabij de werkplek aan te bieden, maak je het voor werknemers makkelijker om sport in hun dagelijkse routine te integreren.",
            icon: CheckIcon,
        },
    ];

    const courses = [
        {
            name: "Teambuilding",
            icon: "/assets/business/maatwerk-organisatie.jpg",
            route: route("course.team-building"),
        },
        {
            name: "Inspirerende lezing",
            icon: "/assets/business/inspiring-lectures.jpg",
            route: route("course.inspiring-lectures"),
        },
        {
            name: "Individuele trajecten",
            icon: "/assets/business/individual-traject.jpg",
            route: route("course.company-fitness"),
        },
    ];

    return (
        <Blog>
            <Head>
                <title>
                    Bedrijfsfitness - WE ARE Personal Training Uden en Schijndel
                </title>
                <link rel="canonical" href={route("business")} />

                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1"
                />
                <meta property="og:locale" content="nl_NL" />

                <meta
                    name="description"
                    content="Jouw transformatie binnen 12 weken? Bij WE ARE personal training Uden en Schijndel regelen we het! Geen resultaat = geld terug!"
                />
                <meta
                    property="og:description"
                    content="Jouw transformatie binnen 12 weken? Bij WE ARE personal training Uden en Schijndel regelen we het! Geen resultaat = geld terug!"
                />
                <meta property="og:url" content={route("business")} />
                <meta property="og:type" content="website" />
                <meta
                    property="og:title"
                    content="Bedrijfsfitness - WE ARE Personal Training Uden en Schijndel"
                />
                <meta
                    property="og:image"
                    content="/assets/personal-training-uden-schijndel.png"
                />
            </Head>

            <div className="bg-black">
                <Hero homepage={false}></Hero>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="block text-white mx-auto font-custom italic font-bold uppercase max-w-4xl xs:text-4xl sm:text-4xl md:text-4xl lg:text-5xl mt-8">
                            Bedrijfsfitness: de sleutel tot gezondere en
                            productievere werknemers
                        </h2>
                        <p className="mt-4 text-lg text-gray-100">
                            In het huidige bedrijfsleven wordt steeds meer
                            aandacht besteed aan de gezondheid en het welzijn
                            van werknemers. Een van de meest effectieve manieren
                            om dit te bevorderen is door bedrijfsfitness aan te
                            bieden. Laten we eens kijken naar de voordelen van
                            bedrijfsfitness en waarom het een slimme investering
                            is voor elke werkgever.
                        </p>
                    </div>

                    <div className="overflow-hidden py-24 sm:py-32">
                        <div className="mx-auto max-w-7xl px-6 lg:px-8">
                            <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                                De{" "}
                                <span className="text-we-are-yellow">
                                    impact
                                </span>{" "}
                                van verzuim op bedrijven
                            </p>
                            <p className="mt-6 text-lg leading-8 text-white">
                                Verzuim is een groot probleem voor veel
                                organisaties. Volgens cijfers van het Centraal
                                Bureau voor de Statistiek (CBS) was het
                                ziekteverzuimpercentage in 2022 gemiddeld 4,8%.
                                Dit betekent dat op elke 100 werkdagen,
                                werknemers gemiddeld 4,8 dagen afwezig waren
                                door ziekte. De kosten hiervan voor het
                                bedrijfsleven lopen in de miljarden euro's per
                                jaar.
                            </p>
                            <p className="mt-6 text-lg leading-8 text-white">
                                Ook obese werknemers zijn zowel vaker als
                                langduriger ziek. Zo verzuimen in Nederland
                                werknemers met obesitas gemiddeld 1,8 keer meer
                                werkdagen (CBS, 2007) en 14 dagen per jaar meer
                                (Jans et al., 2007) dan werknemers met een
                                normaal gewicht. Ze verzuimen gemiddeld 1,3 keer
                                vaker en 11 dagen per verzuimperiode langer
                                (CBS, 2007; Jans et al., 2007).
                            </p>
                            <p className="mt-6 text-lg leading-8 text-white">
                                Hierbij een voorbeeld van mogelijke
                                verzuimkosten per dag - uitgaande van een
                                gemiddeld jaarinkomen in Nederland van 35.000
                                euro (cijfers CPB, 2014).
                            </p>
                            <p className="mt-6 text-lg leading-8 text-white">
                                <strong>
                                    Kosten zieke/verzuimende werknemer per dag:
                                </strong>
                            </p>
                            <table className="mt-6 text-lg leading-8 text-white border-collapse border border-gray-400 w-full">
                                <tbody>
                                    <tr className="border border-gray-400">
                                        <td className="border border-gray-400 p-2">
                                            Loondoorbetaling
                                        </td>
                                        <td className="border border-gray-400 p-2">
                                            € 153
                                        </td>
                                    </tr>
                                    <tr className="border border-gray-400">
                                        <td className="border border-gray-400 p-2">
                                            Kosten vervanging
                                        </td>
                                        <td className="border border-gray-400 p-2">
                                            € 146
                                        </td>
                                    </tr>
                                    <tr className="border border-gray-400">
                                        <td className="border border-gray-400 p-2">
                                            Verlies productie / dienst
                                        </td>
                                        <td className="border border-gray-400 p-2">
                                            € 43
                                        </td>
                                    </tr>
                                    <tr className="border border-gray-400">
                                        <td className="border border-gray-400 p-2">
                                            Kosten arbodienstverlening
                                        </td>
                                        <td className="border border-gray-400 p-2">
                                            € 60
                                        </td>
                                    </tr>
                                    <tr className="border border-gray-400">
                                        <td className="border border-gray-400 p-2">
                                            Verzuimbegeleiding
                                        </td>
                                        <td className="border border-gray-400 p-2">
                                            € 7,50
                                        </td>
                                    </tr>
                                    <tr className="border border-gray-400">
                                        <td className="border border-gray-400 p-2">
                                            <strong>
                                                Totale verzuimkosten:
                                            </strong>
                                        </td>
                                        <td className="border border-gray-400 p-2">
                                            <strong>€ 410 per dag</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <p className="mt-6 text-lg leading-8 text-white">
                                Zoals eerder vermeld, uit een onderzoek is
                                gebleken dat werknemers met obesitas en/of een
                                ongezonde leefstijl 14 dagen per jaar meer
                                verzuimen. Dit komt neer op ongeveer €5740,- per
                                werknemer per jaar!
                            </p>
                        </div>
                        <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-24 xs:mt-8 sm:mt-8 md:mt-24 lg:mt-24">
                            <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                                Hoe je met onze hulp het{" "}
                                <span className="text-we-are-yellow">
                                    verzuim binnen uw bedrijf kan verlagen
                                </span>{" "}
                            </p>

                            <p className="mt-6 text-lg leading-8 text-white">
                                Regelmatige lichaamsbeweging heeft tal van
                                gezondheidsvoordelen die direct bijdragen aan
                                een lager ziekteverzuim:
                            </p>
                            <ol className="mt-6 text-lg leading-8 text-white list-decimal">
                                <li>Versterkt het immuunsysteem</li>
                                <li>Vermindert stress en burn-out klachten</li>
                                <li>
                                    Verbetert de algehele conditie en
                                    uithoudingsvermogen
                                </li>
                                <li>
                                    Verlaagt het risico op chronische ziekten
                                </li>
                            </ol>
                        </div>
                        {/*  */}
                        <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-24 xs:mt-8 sm:mt-8 md:mt-24 lg:mt-24">
                            <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                                Waarom zou u gebruik maken{" "}
                                <span className="text-we-are-yellow">
                                    van ons aanbod
                                </span>{" "}
                                ?
                            </p>
                            <dl className="mt-10 max-w-xl space-y-8 text-base leading-7 lg:max-w-none">
                                {features.map((feature) => (
                                    <div
                                        key={feature.name}
                                        className="relative pl-9"
                                    >
                                        <dt className="inline font-semibold text-lg leading-6 text-white">
                                            <feature.icon
                                                aria-hidden="true"
                                                className="absolute left-1 top-1 h-5 w-5 text-green-600"
                                            />
                                            {feature.name}
                                        </dt>{" "}
                                        <dd className="mt-2 text-white">
                                            {feature.description}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                        <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-24 xs:mt-8 sm:mt-8 md:mt-24 lg:mt-24">
                            <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                                Conclusie
                            </p>
                            <p className="mt-6 text-lg leading-8 text-white">
                                Bedrijfsfitness is meer dan alleen een leuke
                                extra voor werknemers. Het is een strategische
                                investering die leidt tot gezondere, gelukkigere
                                en productievere medewerkers. Door het
                                faciliteren van sport en beweging op de
                                werkvloer, creëer je een win-win situatie voor
                                zowel de werkgever als de werknemer. Het is tijd
                                om in actie te komen en de voordelen van
                                bedrijfsfitness te omarmen!
                            </p>
                        </div>
                        <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-24 xs:mt-8 sm:mt-8 md:mt-24 lg:mt-24">
                            <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                                Ons aanbod
                            </p>
                            <p className="mt-6 text-lg leading-8 text-white">
                                Hieronder vind u ons aanbod, klik op de knop om
                                meer informatie te krijgen over de verschillende
                                mogelijkheden. Voor vragen kunt u altijd contact
                                opnemen zodat we u netjes te woord kunnen staan.
                            </p>
                        </div>
                        {/* Buttons */}
                        <div
                            className={`relative xs:pt-8 sm:pt-8 md:pt-16 lg:pt-16`}
                        >
                            <div className="relative max-w-7xl mx-auto">
                                <div className="lg:mt-24 md:mt-12 sm:mt-12 xs:mt-12 max-w-lg mx-auto grid gap-5 lg:grid-cols-3 lg:max-w-none">
                                    {courses.map((course, key) => (
                                        <div
                                            key={key}
                                            className="flex flex-col rounded-lg shadow-lg overflow-hidden"
                                        >
                                            <div className="flex-shrink-0">
                                                <img
                                                    className="mx-auto h-48 bject-cover"
                                                    src={course.icon}
                                                    alt="Personal training Uden en Schijndel"
                                                />
                                            </div>
                                            <div className="flex-1 bg-black p-6 flex flex-col justify-between">
                                                <div className="flex-1">
                                                    <div className="block mt-2">
                                                        <p className="text-xl font-bold text-center text-gray-100">
                                                            {course.name}
                                                        </p>
                                                        <p className="text-lg text-center mt-2 text-gray-100">
                                                            {course.description}
                                                        </p>
                                                    </div>
                                                </div>
                                                <a
                                                    href={course.route}
                                                    className="mt-4 text-center bg-we-are-purple hover:bg-we-are-purple-dark text-white font-semibold py-2 px-4 rounded-md uppercase"
                                                >
                                                    Meer informatie
                                                </a>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Contact />
            </div>
        </Blog>
    );
}
